import { useState } from 'react';
import toast from 'react-hot-toast';
import { Shield, RefreshCw, CheckSquare, BarChart3, ChevronDown, ChevronUp, Download, Trash2, CheckCircle } from 'lucide-react';
import { AdminStats, Entry, Game, Pick } from '../types';
import { getAdminStats, updateGameResults, finalizeResults, fetchDateEntries, fetchGames, deleteEntry, markEntryAsPaid } from '../services/api';
import { format } from 'date-fns';

const Admin = () => {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  // Entry row component with expandable picks
  const EntryRow = ({ entry }: { entry: Entry }) => {
    const [showPicks, setShowPicks] = useState(false);
    const entryScore = calculateEntryScore(entry);
    
    return (
      <>
        <tr>
          <td className="px-4 py-3 text-sm font-medium text-gray-900">{entry.xHandle}</td>
          <td className="px-4 py-3 text-sm text-gray-600 font-mono" title={entry.xrplAddress}>
            {entry.xrplAddress.slice(0,8)}...{entry.xrplAddress.slice(-6)}
          </td>
          <td className="px-4 py-3 text-sm text-gray-900">{entry.tieBreakTotalScore}</td>
          <td className="px-4 py-3">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              entry.paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {entry.paid ? 'Paid' : 'Pending'}
            </span>
          </td>
          <td className="px-4 py-3 text-sm font-bold text-gray-900">
            {entryScore !== null ? `${entryScore}/${entry.picks?.length || 0}` : '-'}
          </td>
          <td className="px-4 py-3">
            <button
              onClick={() => setShowPicks(!showPicks)}
              className="flex items-center text-sm text-blue-600 hover:text-blue-800"
            >
              {entry.picks?.length || 0} picks
              {showPicks ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
            </button>
          </td>
          <td className="px-4 py-3 text-sm text-gray-500">{new Date(entry.createdAt).toLocaleString()}</td>
          <td className="px-4 py-3">
            <div className="flex items-center space-x-2">
              {!entry.paid && (
                <button
                  onClick={() => handleMarkAsPaid(entry.id, entry.xHandle)}
                  className="inline-flex items-center p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
                  title="Mark as paid"
                  disabled={loading}
                >
                  <CheckCircle className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => handleDeleteEntry(entry.id, entry.xHandle)}
                className="inline-flex items-center p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                title="Delete entry"
                disabled={loading}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </td>
        </tr>
        {showPicks && entry.picks && entry.picks.length > 0 && (
          <tr>
            <td colSpan={8} className="px-4 py-2 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {entry.picks.map((pick: Pick) => {
                  const game = games.find(g => g.id === pick.gameId);
                  const isCorrect = getPickResult(pick, game);
                  return (
                    <div key={pick.gameId} className={`p-2 rounded text-sm border ${
                      isCorrect === true ? 'bg-green-50 border-green-200 text-green-800' :
                      isCorrect === false ? 'bg-red-50 border-red-200 text-red-800' :
                      'bg-gray-50 border-gray-200 text-gray-600'
                    }`}>
                      <div className="font-medium">
                        {game ? `${game.awayTeam} @ ${game.homeTeam}` : `Game ${pick.gameId}`}
                      </div>
                      <div className="text-xs">
                        Picked: {game ? (pick.pick === 'home' ? game.homeTeam : game.awayTeam) : pick.pick}
                        {game?.status === 'final' && (
                          <span className="ml-2">
                            ({game.awayScore}-{game.homeScore})
                            {isCorrect === true && ' ✓'}
                            {isCorrect === false && ' ✗'}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </td>
          </tr>
        )}
      </>
    );
  };

  // Calculate entry score based on correct picks
  const calculateEntryScore = (entry: Entry): number | null => {
    if (!entry.picks || !games.length) return null;
    
    let correctPicks = 0;
    let completedGames = 0;
    
    entry.picks.forEach((pick: Pick) => {
      const game = games.find(g => g.id === pick.gameId);
      if (game?.status === 'final') {
        completedGames++;
        if (getPickResult(pick, game) === true) {
          correctPicks++;
        }
      }
    });
    
    return completedGames > 0 ? correctPicks : null;
  };

  // Determine if a pick was correct
  const getPickResult = (pick: Pick, game?: Game): boolean | null => {
    if (!game || game.status !== 'final' || !game.winner) return null;
    return pick.pick === game.winner;
  };

  // Calculate tie-breaker difference (for final game total score)
  const calculateTieBreakDiff = (entry: Entry): number | null => {
    const finalGame = games.find(g => g.isFinalGame && g.status === 'final');
    if (!finalGame || finalGame.homeScore === undefined || finalGame.awayScore === undefined) {
      return null;
    }
    
    const actualTotal = finalGame.homeScore + finalGame.awayScore;
    return Math.abs(entry.tieBreakTotalScore - actualTotal);
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await loadStats();
      setAuthenticated(true);
      toast.success('Authentication successful');
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const [statsData, entriesData, gamesData] = await Promise.all([
        getAdminStats(password, selectedDate),
        fetchDateEntries(password, selectedDate),
        fetchGames(selectedDate)
      ]);
      setStats(statsData);
      setEntries(entriesData);
      setGames(gamesData.games);
    } catch (error) {
      throw error;
    }
  };

  const handleUpdateGames = async () => {
    try {
      setLoading(true);
      await updateGameResults(password);
      await loadStats();
      toast.success('Game results updated');
    } catch (error) {
      toast.error('Failed to update game results');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalize = async () => {
    if (!confirm('Are you sure you want to finalize results? This cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      await finalizeResults(password, selectedDate);
      await loadStats();
      toast.success('Results finalized');
    } catch (error) {
      toast.error('Failed to finalize results');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async (entryId: string, xHandle: string) => {
    if (!window.confirm(`Are you sure you want to delete the entry for ${xHandle}? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      const result = await deleteEntry(entryId);
      toast.success(`Entry deleted successfully. Removed ${result.deletedPicks} picks.`);
      // Refresh the data
      await loadStats();
    } catch (error: any) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.error || 'Failed to delete entry');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (entryId: string, xHandle: string) => {
    if (!window.confirm(`Mark the entry for ${xHandle} as PAID? This will update the entry status.`)) {
      return;
    }

    try {
      setLoading(true);
      await markEntryAsPaid(entryId);
      toast.success(`Entry for ${xHandle} marked as paid successfully.`);
      // Refresh the data
      await loadStats();
    } catch (error: any) {
      console.error('Mark as paid error:', error);
      toast.error(error.response?.data?.error || 'Failed to mark entry as paid');
    } finally {
      setLoading(false);
    }
  };

  const exportEntriesWithTeams = () => {
    const getTeamNameForPick = (gameId: string, pick: 'home' | 'away'): string => {
      const game = games.find(g => g.id === gameId);
      if (!game) return `${pick} team`;
      return pick === 'home' ? game.homeTeam : game.awayTeam;
    };

    // Create clean CSV with team names
    let csvContent = 'Entry ID,X Handle,XRPL Address,Tie-break Guess,Paid,Score,';
    
    // Add pick columns
    const maxPicks = Math.max(...entries.map(entry => entry.picks?.length || 0));
    for (let i = 1; i <= maxPicks; i++) {
      csvContent += `Pick ${i},`;
    }
    csvContent += 'Created At\n';

    entries.forEach((entry) => {
      const entryScore = calculateEntryScore(entry);
      const scoreText = entryScore !== null ? `${entryScore}/${entry.picks?.length || 0}` : '-';
      
      let row = [
        entry.id,
        entry.xHandle,
        entry.xrplAddress,
        entry.tieBreakTotalScore,
        entry.paid ? 'Yes' : 'No',
        scoreText
      ];

      // Add picks
      if (entry.picks) {
        entry.picks.forEach((pick) => {
          const teamPicked = getTeamNameForPick(pick.gameId, pick.pick);
          row.push(teamPicked);
        });
      }
      
      // Fill empty pick columns
      while (row.length < 6 + maxPicks) {
        row.push('');
      }
      
      row.push(new Date(entry.createdAt).toLocaleString());
      csvContent += row.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `nba-entries-${selectedDate}.csv`;
    link.click();
    
    toast.success('Entries exported with team names!');
  };

  if (!authenticated) {
    return (
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-center mb-6">
            <Shield className="h-12 w-12 text-indigo-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900">Admin Access</h1>
            <p className="text-gray-600">Enter admin password to continue</p>
          </div>

          <form onSubmit={handleAuth} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full"
            >
              {loading ? (
                <div className="flex items-center">
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Authenticating...
                </div>
              ) : (
                'Login'
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">NBA Pick'em Admin</h1>
          <p className="text-gray-600">Contest management and statistics</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="input"
          />
          <button
            onClick={loadStats}
            disabled={loading}
            className="btn btn-secondary"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <BarChart3 className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">{stats.totalEntries}</div>
                <div className="text-sm text-gray-500">Total Entries</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <CheckSquare className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">{stats.paidEntries}</div>
                <div className="text-sm text-gray-500">Paid Entries</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="text-green-600 text-xl font-bold">$</div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">{stats.totalRevenue.toLocaleString()}</div>
                <div className="text-sm text-gray-500">Revenue ($NUTS)</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="text-indigo-600 text-xl font-bold">#</div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">{stats.completedGames}/{stats.gamesCount}</div>
                <div className="text-sm text-gray-500">Games Complete</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Actions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Admin Actions</h2>
        
        <div className="space-y-4">
          <button
            onClick={handleUpdateGames}
            disabled={loading}
            className="btn btn-primary w-full md:w-auto"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Update Game Results
          </button>

          <button
            onClick={handleFinalize}
            disabled={loading}
            className="btn btn-secondary w-full md:w-auto ml-0 md:ml-4"
          >
            <CheckSquare className="h-4 w-4 mr-2" />
            Finalize Week Results
          </button>

          <button
            onClick={exportEntriesWithTeams}
            disabled={loading || entries.length === 0}
            className="btn btn-outline w-full md:w-auto ml-0 md:ml-4"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Picks
          </button>
        </div>

        <div className="mt-6 text-sm text-gray-600">
          <p className="mb-2">
            <strong>Update Game Results:</strong> Fetches the latest NFL game results and updates the database.
          </p>
          <p className="mb-2">
            <strong>Finalize Week Results:</strong> Calculates final standings and distributes prizes. This action cannot be undone.
          </p>
          <p>
            <strong>Export Picks:</strong> Downloads a clean CSV file with all entries and their team picks.
          </p>
        </div>
      </div>

      {/* Entries Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Entries ({selectedDate})</h2>
          <span className="text-sm text-gray-500">{entries.length} entries</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Player</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">XRPL Address</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tie-break</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Picks</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.map(entry => (
                <EntryRow key={entry.id} entry={entry} />
              ))}
              {entries.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500">No entries yet for this week.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Important Notes */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <Shield className="h-5 w-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">NBA Contest Notes</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Entry fee: 1000 $NUTS</li>
                <li>Tie-breaker: Total combined score of final NBA game</li>
                <li>Daily format: Contest runs for each day's games</li>
                <li>Payouts: 1st, 2nd, and 3rd place winners</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Leaderboard/Winners Section */}
      {entries.length > 0 && games.some(g => g.status === 'final') && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <BarChart3 className="h-5 w-5 mr-2 text-yellow-500" />
              Current Standings & Prize Distribution
            </h2>
          </div>
          
          <div className="p-6">
            {(() => {
              // Calculate standings
              const standings = entries
                .map(entry => {
                  const score = calculateEntryScore(entry);
                  const tieBreakDiff = calculateTieBreakDiff(entry);
                  return {
                    entry,
                    score: score || 0,
                    tieBreakDiff,
                    paid: entry.paid
                  };
                })
                .filter(s => s.paid) // Only paid entries
                .sort((a, b) => {
                  if (a.score !== b.score) return b.score - a.score; // Higher score wins
                  // Handle null tie-break diffs (put them at end)
                  if (a.tieBreakDiff === null && b.tieBreakDiff === null) return 0;
                  if (a.tieBreakDiff === null) return 1;
                  if (b.tieBreakDiff === null) return -1;
                  return a.tieBreakDiff - b.tieBreakDiff; // Lower tie-break diff wins
                })
                .map((standing, index) => ({ ...standing, rank: index + 1 }));

              if (standings.length === 0) {
                return <div className="text-center text-gray-500">No paid entries to rank yet.</div>;
              }

              const paidEntries = standings.length;
              const totalPot = paidEntries * 1000; // 1000 $NUTS per entry
              const prizes = {
                total: totalPot,
                first: Math.floor(totalPot * 0.5), // 50%
                second: Math.floor(totalPot * 0.3), // 30% 
                third: Math.floor(totalPot * 0.2)   // 20%
              };

              return (
                <div>
                  {/* Prize Pool Summary */}
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 mb-6 border border-yellow-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                      <span className="text-green-600 text-xl font-bold mr-2">$</span>
                      Prize Pool
                    </h3>
                    <div className="grid grid-cols-4 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-gray-900">{prizes.total}</div>
                        <div className="text-sm text-gray-600">Total $NUTS</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-yellow-600">{prizes.first}</div>
                        <div className="text-sm text-gray-600">1st Place (50%)</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-gray-500">{prizes.second}</div>
                        <div className="text-sm text-gray-600">2nd Place (30%)</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-amber-600">{prizes.third}</div>
                        <div className="text-sm text-gray-600">3rd Place (20%)</div>
                      </div>
                    </div>
                  </div>

                  {/* Standings Table */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Player</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">XRPL Address</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tie-break</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tie-break Diff</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prize</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {standings.map((standing) => {
                          const prizeAmount = standing.rank === 1 ? prizes.first :
                                            standing.rank === 2 ? prizes.second :
                                            standing.rank === 3 ? prizes.third : 0;
                          
                          return (
                            <tr key={standing.entry.id} className={standing.rank <= 3 ? 'bg-yellow-50' : ''}>
                              <td className="px-4 py-3 text-sm">
                                <span className={`font-bold ${
                                  standing.rank === 1 ? 'text-yellow-600' :
                                  standing.rank === 2 ? 'text-gray-500' :
                                  standing.rank === 3 ? 'text-amber-600' :
                                  'text-gray-900'
                                }`}>
                                  #{standing.rank}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {standing.entry.xHandle}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                                {standing.entry.xrplAddress.slice(0, 8)}...{standing.entry.xrplAddress.slice(-6)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 font-bold">
                                {standing.score}/{standing.entry.picks?.length || 0}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {standing.entry.tieBreakTotalScore}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {standing.tieBreakDiff !== null ? standing.tieBreakDiff : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm font-bold text-green-600">
                                {prizeAmount > 0 ? `${prizeAmount} $NUTS` : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
